// Copyright 2018 Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import React from "react";
import _ from "lodash";

// swagger-ui
import SwaggerUI from "swagger-ui";
import "swagger-ui/dist/swagger-ui.css";

// semantic-ui
import { Container, Header, Icon } from "semantic-ui-react";

// services
import { isAdmin, isRegistered } from "services/self";
import { getApiSwaggerInfo, getApi } from "services/api-catalog";
// components
import ApisMenu from "components/ApisMenu";
import SwaggerLayoutPlugin from "components/SwaggerUiLayout";
import PageWithSidebar from "components/PageWithSidebar";

// state
import { store } from "services/state.js";
import { observer } from "mobx-react";
import {
  getHostList,
  getApiList,
  getMES16ApiColor,
  getMES15ApiColor,
  getMES15Host,
  getEnvide,
  geApiGatewayApiColor,
} from "services/developer";
import {
  resetSwaggerApiName,
  limitConcurrentRequests,
  isMES16OauthAPI,
  isMES16ErpAPI,
} from "utils/function-utils";
import { REQUEST_LIMIT } from "env.config";
export default observer(
  class ApisPage extends React.Component {
    containerRef = React.createRef();
    removed = false;
    cachedSwagger = null;
    apiInfo = {
      stage: null,
      id: null,
    };
    swaggerData = {};
    globalAuthorizedList = [];
    constructor(props) {
      super(props);
      this.state = {
        apiList: [],
        currentHost: { Apis: [] },
        hostConfig: {},
        loaded: false,
      };
    }

    // Initial load
    componentDidMount() {
      this.getApiListGroupByHost().then(() => {
        this.updateApi();
      });
    }

    componentDidUpdate() {
      this.updateApi();
    }

    componentWillUnmount() {
      this.removed = true;
    }

    async getOtherConfig(apiList) {
      const envideList = apiList.map((api) => api.Envide);
      const colorPromises = envideList.map((envide) =>
        getMES15ApiColor(envide)
      );
      const configPromises = envideList.map((envide) => getMES15Host(envide));
      const colorRes = await Promise.all(colorPromises);
      const configRes = await Promise.all(configPromises);
      apiList.forEach((api, index) => {
        api.Active = colorRes[index].active;
      });
      const hostConfig = {};
      configRes.forEach((config) => {
        Object.assign(hostConfig, config);
      });
      // filter apiList by color
      apiList = this.filterApiListByColor(apiList);
      return { newApiList: apiList, hostConfig };
    }

    async getVersionConfig(apiList) {
      const hostList = apiList.map((api) => api.Host);
      const envideList = apiList
        .map((api) => api.Envide)
        .filter((item, index, self) => self.indexOf(item) === index);
      const res = await limitConcurrentRequests(
        (url) => getMES16ApiColor(url),
        [...hostList],
        REQUEST_LIMIT
      );
      const apiGatewayApiVersion = await limitConcurrentRequests(
        (envide) => geApiGatewayApiColor(envide),
        [...envideList],
        REQUEST_LIMIT
      );
      apiList.forEach((api) => {
        api.Version = res[api.Host]?.version;
        api.Active = res[api.Host]?.color;
      });
      apiList.forEach((api) => {
        api.ApiGatewayVersion =
          apiGatewayApiVersion && apiGatewayApiVersion[api.Envide];
        api.Apis = api.Apis.filter((_item) => api.Active).filter((apiItem) => {
          if (isMES16OauthAPI(apiItem)) {
            return new RegExp(
              `.*-api-${api.ApiGatewayVersion?.oauthApi}$`
            ).test(apiItem);
          }
          if (isMES16ErpAPI(apiItem)) {
            return new RegExp(`.*-api-${api.ApiGatewayVersion?.erpApi}$`).test(
              apiItem
            );
          }
          return new RegExp(`.*-${api.Version}-${api.Active}$`).test(apiItem);
        });
        // }
      });
      return apiList.filter((api) => api.Apis.length);
      // filter apilist by version
    }

    sortApiList(apiList) {
      apiList.sort((first, second) => {
        return first.Name.localeCompare(second.Name);
      });
      return apiList;
    }

    filterApiListByColor(apiList) {
      if (!apiList || !apiList.length) {
        return apiList;
      }
      apiList.forEach((api) => {
        if (api.Active) {
          api.Apis = api.Apis.filter((apiItem) =>
            new RegExp(`.*-${api.Active}$`).test(apiItem)
          );
        }
      });
      return apiList.filter((api) => api.Active && api.Apis.length);
    }

    getInitialHost(apiList) {
      const apiStageList = apiList.map((host) => host.Stage);
      const devIndex = apiStageList.indexOf("DEV");
      const sqaIndex = apiStageList.indexOf("SQA");
      const uatIndex = apiStageList.indexOf("UAT");
      const previewIndex = apiStageList.indexOf("PREVIEW");
      const prodIndex = apiStageList.indexOf("PROD");
      if (devIndex > -1) {
        return apiList[devIndex];
      } else if (sqaIndex > -1) {
        return apiList[sqaIndex];
      } else if (uatIndex > -1) {
        return apiList[uatIndex];
      } else if (previewIndex > -1) {
        return apiList[previewIndex];
      } else if (prodIndex > -1) {
        return apiList[prodIndex];
      }
      return apiList[0];
    }

    async getMES15ApiList(apiList, hostList) {
      if (!apiList.length || !hostList.length) {
        return [];
      }
      hostList.forEach((host) => {
        apiList.forEach((api) => {
          if (host.Envide === api.Envide) {
            Object.assign(api, { ...host });
          }
        });
      });
      apiList = apiList.filter((api) => api.Host);
      const { newApiList, hostConfig } = await this.getOtherConfig(apiList);
      this.setState({ hostConfig });
      return newApiList;
    }

    async getMES16ApiList(apiList) {
      if (!apiList.length) {
        return [];
      }
      return await this.getVersionConfig(apiList);
    }

    async getApiListGroupByHost() {
      const admin = isAdmin();
      const envideConfig = {};
      const ENVIDE = await getEnvide();
      for (let key in ENVIDE) {
        if (!ENVIDE[key].MES) {
          envideConfig[key] = ENVIDE[key];
        }
      }
      let [apiList, hostList] = await Promise.all([
        getApiList({ admin, envideConfig }),
        getHostList(),
      ]);
      const MES15List = apiList.filter((api) => api.MES === "MES15");
      const MES16List = apiList.filter((api) => api.MES === "MES16");

      const MES15ApiList = await this.getMES15ApiList(MES15List, hostList);
      const MES16ApiList = await this.getMES16ApiList(MES16List);

      if (!MES15ApiList.length && !MES16ApiList.length) {
        this.setState({ loaded: true });
        return [];
      }
      const newApiList = this.sortApiList(MES15ApiList.concat(MES16ApiList));
      const currentHost = this.getInitialHost(newApiList);
      this.setState({ loaded: true, apiList: newApiList, currentHost });
      return MES15ApiList.concat(MES16ApiList);
    }

    validateSwagger(swagger) {
      let swaggerTitle = swagger.info.title;
      const { MES, Version, Active, Permission, ApiGatewayVersion } =
        this.state.currentHost;
      swaggerTitle = resetSwaggerApiName(
        MES,
        swaggerTitle,
        Version,
        ApiGatewayVersion,
        Active
      );
      if (Permission) {
        const title = swagger?.info?.title;
        const filterdPermission = Permission.filter(
          (p) => title.indexOf(p.api) > -1
        );
        if (filterdPermission && filterdPermission.length) {
          let paths = swagger.paths;
          const pList = filterdPermission;
          let pObj = {};
          for (let i = 0, len = pList.length; i < len; i++) {
            const item = pList[i];
            if (pObj.hasOwnProperty(item.path)) {
              pObj[item.path].push(item.title);
            } else {
              pObj[item.path] = [item.title];
            }
          }
          let result = Object.entries(paths).filter(([k, v]) => {
            return pObj.hasOwnProperty(k);
          });
          result.forEach(([k, v]) => {
            const methodList = Object.keys(v);

            methodList.forEach((item) => {
              if (pObj[k].indexOf(item) < 0) {
                delete v[item];
              }
            });
          });
          const resultObj = Object.fromEntries(result);
          swagger.paths = resultObj;
        }
      }
      swagger.tags = this.extractTags(swagger.paths);
      swagger.info.title = swaggerTitle;
    }

    extractTags(obj) {
      let res = [];
      for (let key in obj) {
        if (obj[key] && typeof obj[key] === "object") {
          const subObj = obj[key];
          if (subObj && typeof subObj === "object") {
            for (let key in subObj) {
              const subSubObj = subObj[key];
              if (subSubObj.tags && Array.isArray(subSubObj.tags)) {
                res = res.concat(subSubObj.tags);
              }
            }
          }
        }
      }
      return[...new Set(res)].map((item) => {
        return { name: item };
      });
    }

    setCustomDomain(swagger) {
      const currentHost = this.state.currentHost;
      if (currentHost?.MES === "MES16") {
        swagger.host = currentHost.Host;
        if (swagger.info.title.indexOf("oauth-api") > -1) {
          swagger.basePath = "/oauth";
        }
        if (swagger.info.title.indexOf("erp-api") > -1) {
          swagger.basePath = "/erp-api";
        }
        return;
      }
      const hostObject = this.state.hostConfig;
      for (let key in hostObject) {
        if (`${swagger.host}${swagger.basePath}` === key) {
          const url = hostObject[key];
          if (url.indexOf("/") > -1) {
            const i = url.indexOf("/");
            swagger.host = url.slice(0, i);
            swagger.basePath = url.slice(i);
          } else {
            swagger.host = url;
            swagger.basePath = "";
          }
        }
      }
    }

    updateApi() {
      return getApi(
        this.props.match.params.apiId || "ANY",
        true,
        this.props.match.params.stage,
        false,
        this.state.currentHost
      ).then((api) => {
        if (api && !this.removed) {
          const cell = {
            shouldPreauthorizeApiKey: false,
            preauthorizeApiKey: () => {
              cell.shouldPreauthorizeApiKey = true;
            },
          };
          const { id, stage } = api;
          this.retrieveApiSwaggerData(id, stage).then((swagger) => {
            if (!swagger || !swagger.info || !swagger.info.title) return;
            const copiedSwagger = _.cloneDeep(swagger);
            this.validateSwagger(copiedSwagger);
            this.setCustomDomain(copiedSwagger);
            api.swagger = copiedSwagger;
            const swaggerUiConfig = {
              domNode: this.containerRef.current,
              plugins: [SwaggerLayoutPlugin],
              supportedSubmitMethods: [],
              spec: api.swagger,
              onComplete: () => cell.preauthorizeApiKey(),
            };
            if (isRegistered()) {
              delete swaggerUiConfig.supportedSubmitMethods;
            }

            const uiHandler = SwaggerUI(swaggerUiConfig);
            cell.preauthorizeApiKey = () => {
              if (store.apiKey) {
                uiHandler.preauthorizeApiKey("api_key", store.apiKey);
              }
            };
            if (cell.shouldPreauthorizeApiKey) {
              cell.preauthorizeApiKey();
            }
          });
        }
      });
    }

    async retrieveApiSwaggerData(apiId, stage) {
      let apiSwaggerData = null;
      const bucketData = await this.cacheApiSwaggerData(apiId, stage);
      if (bucketData.status === 200) {
        apiSwaggerData = bucketData.data;
      }
      return apiSwaggerData;
    }

    cacheApiSwaggerData(apiId, stage) {
      if (
        this.cachedSwagger &&
        this.apiInfo.id === apiId &&
        this.apiInfo.stage === stage
      ) {
        return this.cachedSwagger;
      }

      this.apiInfo.id = apiId;
      this.apiInfo.stage = stage;
      this.cachedSwagger = getApiSwaggerInfo(apiId, stage);
      return this.cachedSwagger;
    }

    changeOptions = async (_e, data) => {
      const currentHost = this.state.apiList.filter(
        (host) => host.Host === data.value
      )[0];
      this.setState({ currentHost }, () => {
        this.props.history.push({
          pathname: `/apis`,
        });
        this.cachedSwagger = null;
        this.updateApi();
      });
    };

    render() {
      let errorHeader;
      let errorBody;
      const { loaded, apiList, currentHost } = this.state;
      if (loaded && !apiList.length) {
        this.containerRef.current = null;
        errorHeader = `No APIs Published`;
        errorBody = `Your administrator hasn't added any APIs to your account. Please contact them to publish an API.`;
      }
      return (
        <PageWithSidebar
          sidebarContent={
            <ApisMenu
              loaded={loaded}
              path={this.props.match}
              changeOptions={this.changeOptions}
              currentHost={currentHost}
              apiList={apiList}
            />
          }
          SidebarPusherProps={{ className: "swagger-section" }}
        >
          {errorHeader && errorBody && (
            <>
              <Header
                as="h2"
                icon
                textAlign="center"
                style={{ padding: "40px 0px" }}
              >
                <Icon name="warning sign" circular />
                <Header.Content>{errorHeader}</Header.Content>
              </Header>
              <Container text textAlign="justified">
                <p>{errorBody}</p>
              </Container>
            </>
          )}
          {!errorHeader && (
            <div
              className="swagger-ui-wrap"
              ref={this.containerRef}
              style={{ padding: "0 20px" }}
            ></div>
          )}
        </PageWithSidebar>
      );
    }
  }
);
